import { useCallback, useState, useEffect } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  AdvancedMarkerAnchorPoint,
  CollisionBehavior,
  InfoWindow,
} from '@vis.gl/react-google-maps';
import { BsEvStation } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  FaBolt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaPlug,
  FaCrosshairs,
} from 'react-icons/fa';
import { formatKw } from '../utils/utils';
import {  FaRegIdBadge } from 'react-icons/fa6';
import { ImSpinner2 } from 'react-icons/im';

const MapContent = ({ chargers, selectedChargerId, onChargerSelect }) => {
  const { t } = useTranslation('dashboard');
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [anchorPoint] = useState('BOTTOM');
  const map = useMap();
  const [locating, setLocating] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);

  // Check if map is ready
  useEffect(() => {
    if (map) {
      setIsMapReady(true);
    }
    return () => {
      setIsMapReady(false);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !selectedChargerId || !chargers.length) return;
    
    let isMounted = true;
    
    try {
      const charger = chargers.find((c) => c._id === selectedChargerId);
      if (charger && isMounted) {
        const coords = {
          lat: charger.location.coordinates[1],
          lng: charger.location.coordinates[0],
        };
        // Check if map methods are available before calling
        if (map.panTo && map.setZoom) {
          map.panTo(coords);
          map.setZoom(12);
        }
        setSelectedId(chargers.findIndex((c) => c._id === selectedChargerId));
      }
    } catch (error) {
      // Silently handle errors during unmount
      if (process?.env.NODE_ENV === 'development') {
        console.warn('Map operation error:', error);
      }
    }
    
    return () => {
      isMounted = false;
    };
  }, [map, selectedChargerId, chargers]);

  // Sort chargers by latitude (south-most on top)
  const sortedChargers = chargers
    ? [...chargers].sort(
        (a, b) => b.location.coordinates[1] - a.location.coordinates[1]
      )
    : [];

  const defaultCenter = chargers?.length
    ? {
        lat: chargers[0].location.coordinates[1],
        lng: chargers[0].location.coordinates[0],
      }
    : { lat: 39.9334, lng: 32.8597 }; // Turkey coordinates (Ankara)

  const onMarkerClick = useCallback(
    (id) => {
      if (selectedId === id) {
        setSelectedId(null);
        if (onChargerSelect) {
          onChargerSelect(null);
        }
        return;
      }
      const charger = chargers[id];
      if (!charger) return;
      
      const coords = {
        lat: charger.location.coordinates[1],
        lng: charger.location.coordinates[0],
      };
      
      try {
        if (map && map.panTo && map.setZoom) {
          map.panTo(coords);
          map.setZoom(12);
        }
      } catch (error) {
        // Silently handle errors during unmount
        if (process?.env.NODE_ENV === 'development') {
          console.warn('Map pan error:', error);
        }
      }
      
      setSelectedId(id);
      if (onChargerSelect) {
        onChargerSelect(charger._id);
      }
    },
    [chargers, map, onChargerSelect, selectedId]
  );

  const onMapClick = useCallback(() => {
    setSelectedId(null);
    if (onChargerSelect) {
      onChargerSelect(null);
    }
  }, [onChargerSelect]);

  const getMarkerIcon = (status) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transform: 'scale(1.2)',
        transition: 'all 0.3s ease',
      }}
    >
      <BsEvStation
        size={24}
        color={status === 'ONLINE' ? '#52c41a' : '#ff4d4f'}
        style={{
          filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))',
        }}
      />
    </div>
  );

  // Locate Me handler
  const handleLocateMe = async () => {
    if (!map || !map.panTo || !map.setZoom) return;
    setLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          try {
            if (map && map.panTo && map.setZoom) {
              const { latitude, longitude } = position.coords;
              map.panTo({ lat: latitude, lng: longitude });
              map.setZoom(14);
            }
          } catch (error) {
            if (process?.env.NODE_ENV === 'development') {
              console.warn('Map geolocation error:', error);
            }
          } finally {
            setLocating(false);
          }
        },
        () => {
          setLocating(false);
          alert('Unable to retrieve your location.');
        }
      );
    } else {
      setLocating(false);
      alert('Geolocation is not supported by your browser.');
    }
  };

  // Don't render if no valid chargers
  if (!chargers || chargers.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
        }}
      >
        {t('dashboard:noChargersFound', 'No chargers found')}
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {/* Locate Me Button */}
      <button
        onClick={handleLocateMe}
        disabled={locating || !map}
        style={{
          position: 'absolute',
          bottom: 24,
          right: 24,
          zIndex: 10,
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '50%',
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          cursor: locating || !map ? 'not-allowed' : 'pointer',
        }}
        aria-label="Locate Me"
        title="Locate Me"
      >
        {locating ? (
          <ImSpinner2 className="animate-spin" size={22} />
        ) : (
          <FaCrosshairs size={22} />
        )}
      </button>
      {/* Anchor Point Control Panel */}

      <Map
        defaultZoom={5}
        defaultCenter={defaultCenter}
        mapId="64539ec20bfdb284"
        streetView={false}
        onClick={onMapClick}
        disableDefaultUI
        gestureHandling={'greedy'}
      >
        {isMapReady && sortedChargers
          .filter((charger) => 
            charger?.location?.coordinates && 
            charger.location.coordinates.length >= 2 &&
            typeof charger.location.coordinates[1] === 'number' &&
            typeof charger.location.coordinates[0] === 'number'
          )
          .map((charger, sortedIndex) => {
            // Find the original index for selection logic
            const origIndex = chargers.findIndex((c) => c._id === charger._id);
            // Dynamic zIndex
            let zIndex = sortedIndex;
            if (hoveredId === charger._id) zIndex = chargers.length + 1;
            if (selectedId === origIndex) zIndex = chargers.length + 2;
            return (
              <ChargerAdvancedMarker
                key={charger._id}
                charger={charger}
                isSelected={selectedId === origIndex}
                onClick={() => onMarkerClick(origIndex)}
                getMarkerIcon={getMarkerIcon}
                t={t}
                zIndex={zIndex}
                onMouseEnter={() => setHoveredId(charger._id)}
                onMouseLeave={() => setHoveredId(null)}
                isHovered={hoveredId === charger._id}
                anchorPoint={AdvancedMarkerAnchorPoint[anchorPoint]}
                map={map}
              />
            );
          })}
      </Map>
    </div>
  );
};

MapContent.propTypes = {
  chargers: PropTypes.array.isRequired,
  selectedChargerId: PropTypes.string,
  onChargerSelect: PropTypes.func,
};

const ChargerMap = ({ chargers, selectedChargerId, onChargerSelect }) => {
  return (
    <APIProvider
      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
      libraries={['marker']}
    >
      <MapContent
        chargers={chargers}
        selectedChargerId={selectedChargerId}
        onChargerSelect={onChargerSelect}
      />
    </APIProvider>
  );
};

ChargerMap.propTypes = {
  chargers: PropTypes.array.isRequired,
  selectedChargerId: PropTypes.string,
  onChargerSelect: PropTypes.func,
};

// Add statusColors for tag design
const statusColors = {
  ONLINE: 'green',
  OFFLINE: 'red',
};

const ChargerInfoCard = ({ charger, t }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const images = charger.images || [];
  const acdc = charger.powerType;
  const location = charger.locationData?.[0]?.locationName || '';
  const connectors = charger.connectors || [];
  const operator = charger.operatorName;
  const owner = charger.locationOwnerName;
  const is24hr = charger.is24hrOpen;
  const openingTimes = charger.openingTimes || [];
  const lastOnline = charger.lastOnline
    ? new Date(Number(charger.lastOnline) * 1000)
    : null;
  const lastOffline = charger.lastOffline
    ? new Date(Number(charger.lastOffline) * 1000)
    : null;
  const uptime = charger.todayUptimePercentage ?? 0;

  // Tag design for status
  const statusColor = statusColors[charger.status] || 'gray';

  // Carousel navigation
  const prevImg = (e) => {
    e.stopPropagation();
    setImgIdx((idx) => (idx - 1 + images.length) % images.length);
  };
  const nextImg = (e) => {
    e.stopPropagation();
    setImgIdx((idx) => (idx + 1) % images.length);
  };

  // Format time
  const formatTime = (date) => (date ? date.toLocaleString() : '-');

  return (
    <div className="min-w-[350px] text-[#391E29] [&::-webkit-scrollbar]:w-2  [&::-webkit-scrollbar-track]:rounded-full   [&::-webkit-scrollbar-track]:bg-gray-100   [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500">
      {/* Image Carousel */}
      <div className="relative w-full h-40 flex items-center justify-center">
        {images.length > 0 ? (
          <img
            src={images[imgIdx] || "/NoChargerPlaceholder.png"}
            alt={`${t('dashboard:image')} ${imgIdx + 1}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <img
            src={"/NoChargerPlaceholder.webp"}
            alt={`${t('dashboard:noImage')}`}
            className="w-full h-full object-contain rounded-lg mb-4"
          />
          </div>
        )}
        {/* Status tag (matches ChargerDashboard) */}
        <div
          className={`absolute top-3 left-3 flex items-center gap-1 px-3 py-0.5 rounded-md text-xs font-medium
            ${statusColor === 'green' ? 'bg-green-50 text-green-600 border border-green-200' :
              statusColor === 'red' ? 'bg-red-50 text-red-600 border border-red-200' :
              'bg-gray-50 text-gray-600 border border-gray-200'}
          `}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full
              ${statusColor === 'green' ? 'bg-green-600' :
                statusColor === 'red' ? 'bg-red-600' :
                'bg-gray-600'}
            `}
          />
          {t(`chargerStatus.${charger.status}`, { defaultValue: charger.status })}
        </div>
        {/* Carousel arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImg}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor">
                <path
                  d="M12 15l-6-6 6-6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              onClick={nextImg}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor">
                <path
                  d="M6 3l6 6-6 6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
        {/* Image count */}
        {images.length > 1 && (
          <span className="absolute bottom-2 right-3  text-white text-xs px-2 py-0.5 rounded-full">
            {imgIdx + 1} / {images.length} {t('dashboard:images')}
          </span>
        )}
      </div>
      {/* Card Body */}
      <div className="pb-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold truncate" title={charger.name}>
            {charger.name} ({acdc})
          </h2>
          =
        </div>
        {/* Serial and Capacity */}
        <div className="flex gap-6 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-1 font-semibold text-sm">
              <FaRegIdBadge   className="inline " />{' '}
              <span>{t('dashboard:serialNumber')}</span>
            </div>
            <div className="text-xs  ml-4">{charger.serialNumber}</div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1 font-semibold text-sm">
              <FaBolt className="inline" />{' '}
              <span>{t('dashboard:maxCapacity')}</span>
            </div>
            <div className="text-xs  ml-4">{formatKw(charger.maxCapacity)} kW</div>
          </div>
        </div>
        {/* Residential Area Autopark */}
        <div className="flex gap-6 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-1 font-semibold text-sm">
              <FaMapMarkerAlt className="inline" />{' '}
              <span>{t('dashboard:location')}</span>
            </div>
            <div className="text-xs  ml-4">{location}</div>
          </div>
          {/* Availability */}
          <div className="flex-1">
            <div className="flex items-center gap-1 font-semibold text-sm">
              <FaClock className="inline" />{' '}
              <span>{t('dashboard:availability')}</span>
            </div>
            <div className="text-xs  ml-4">
              {is24hr
                ? t('dashboard:open24Hours')
                : openingTimes.length
                  ? openingTimes.join(', ')
                  : t('dashboard:n_a')}
            </div>
          </div>
        </div>
        {/* Connectors */}
        <div className="mb-2">
          <div className=" text-sm font-semibold mb-1">
            {t('dashboard:connectors')} ({connectors.length})
          </div>
          {connectors.map((conn, idx) => (
            <div
              key={conn._id || idx}
              className="rounded-lg px-2 py-2 mb-1 flex items-center justify-between"
            >
              <div>
                <div className="font-semibold text-sm flex items-center gap-1">
                  <FaPlug className="inline" /> {conn.connectorType}
                </div>
                <div className="text-xs ml-2">{t('dashboard:id')}: {conn.connectorId}</div>
              </div>
              <div className="flex flex-col items-end">
               { conn.status !== 'Ready' && <span
                  className={`px-2 py-0.5 rounded-full text-xs  mb-1 ${conn.status === 'Ready' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}
                >
                  {conn.status}
                </span>}
              </div>
            </div>
          ))}
        </div>
        {/* Residential Area & Owner */}
        <div className="grid grid-cols-2 gap-8 border-t pt-3">
          <div className="flex-1">
            <div className="flex items-center gap-1 font-semibold  text-sm">
              <FaBuilding className="inline" />{' '}
              <span>{t('dashboard:operator')}</span>
            </div>
            <div className="text-xs ml-3 mt-1">{operator}</div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1 font-semibold  text-sm">
              <FaUser className="inline" />{' '}
              <span>{t('dashboard:owner')}</span>
            </div>
            <div className="text-xs ml-3 mt-1">{owner}</div>
          </div>
        </div>
        {/* Footer */}
        <div className="mt-3 text-xs ">
          <div>{t('dashboard:lastOnline')}: {formatTime(lastOnline)}</div>
          <div>{t('dashboard:lastOffline')}: {formatTime(lastOffline)}</div>
          <div>{t('dashboard:todaysUptime')}: {uptime}%</div>
        </div>
      </div>
    </div>
  );
};

const ChargerAdvancedMarker = ({
  charger,
  isSelected,
  onClick,
  getMarkerIcon,
  t,
  zIndex,
  onMouseEnter,
  onMouseLeave,
  isHovered,
  anchorPoint,
  map,
}) => {
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Validate charger location data
  if (!charger?.location?.coordinates || charger.location.coordinates.length < 2) {
    return null;
  }

  // Don't render if map is not available
  if (!map) {
    return null;
  }

  const lat = charger.location.coordinates[1];
  const lng = charger.location.coordinates[0];

  // Validate coordinates are valid numbers
  if (typeof lat !== 'number' || typeof lng !== 'number' || 
      isNaN(lat) || isNaN(lng) ||
      lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  const position = {
    lat,
    lng,
  };

  // Handler to close InfoWindow
  const handleInfowindowCloseClick = (e) => {
    try {
      if (!isMounted) return;
      e?.stopPropagation?.();
      onClick();
    } catch (error) {
      // Silently handle errors during unmount
      if (process?.env.NODE_ENV === 'development') {
        console.warn('InfoWindow close error:', error);
      }
    }
  };

  // Safe click handler
  const handleMarkerClick = (e) => {
    if (!isMounted || !map) return;
    try {
      onClick?.(e);
    } catch (error) {
      if (process?.env.NODE_ENV === 'development') {
        console.warn('Marker click error:', error);
      }
    }
  };

  // Safe mouse handlers
  const handleMouseEnter = (e) => {
    if (!isMounted) return;
    try {
      onMouseEnter?.(e);
    } catch (error) {
      if (process?.env.NODE_ENV === 'development') {
        console.warn('Marker mouse enter error:', error);
      }
    }
  };

  const handleMouseLeave = (e) => {
    if (!isMounted) return;
    try {
      onMouseLeave?.(e);
    } catch (error) {
      if (process?.env.NODE_ENV === 'development') {
        console.warn('Marker mouse leave error:', error);
      }
    }
  };

  // Early return if unmounting to prevent accessing map during cleanup
  if (!isMounted) {
    return null;
  }

  try {
    return (
      <AdvancedMarker
        position={position}
        onClick={handleMarkerClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        zIndex={zIndex}
        style={{
          transform: `scale(${isSelected || isHovered ? 1.2 : 1})`,
          transition: 'transform 0.2s',
        }}
        anchorPoint={anchorPoint}
        collisionBehavior={CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY}
      >
        {getMarkerIcon(charger.status)}
        {isSelected && (
          <InfoWindow
            position={position}
            anchor={position}
            pixelOffset={[0, -2]}
            className=""
            onCloseClick={handleInfowindowCloseClick}
          >
            <ChargerInfoCard charger={charger} t={t} />
          </InfoWindow>
        )}
      </AdvancedMarker>
    );
  } catch (error) {
    // Silently handle errors during unmount or invalid state
    if (process?.env.NODE_ENV === 'development') {
      console.warn('AdvancedMarker render error:', error);
    }
    return null;
  }
};

export default ChargerMap;
